import { FC, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Folder as FolderIcon, FolderPlus, MoreHorizontal, Inbox, Zap,
  Lock, Users,
} from 'lucide-react';
import type { Folder } from '@/api/folders';
import { useFolders } from '@/hooks/useFolders';
import { useAuth } from '@/hooks/useAuth';
import { useTenants } from '@/hooks/useTenants';
import FolderPermDialog from '@/components/folders/FolderPermDialog';
import { InlinePrompt, InlineConfirm } from '@/components/common/InlinePrompt';

// 全局左侧边栏的文件夹树（紧凑版，无标题栏）：
// 当前脑=personal/network 显示一棵；both 分「个人脑」「网络脑」两组。
// 点节点跳 /folders/:id，「未归档」跳 /folders/none（按当前脑）。
interface SidebarFolderTreeProps {
  brainSide: string;
  /** 游客演示只读：隐藏新建/重命名/删除/规则/播种等一切写入口 */
  readOnly?: boolean;
}

interface FolderNode extends Folder {
  children: FolderNode[];
}

// 后端返回 flat 列表，这里按 parent_id 组装成树
function buildTree(folders: Folder[]): FolderNode[] {
  const map = new Map<string, FolderNode>();
  folders.forEach((f) => map.set(f.id, { ...f, children: [] }));
  const roots: FolderNode[] = [];
  map.forEach((node) => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

// 子树内容总数（直属+后代，四表口径与节点计数一致：笔记+知识单元+剪藏+文档）
// （09-26 默认一层收起后暂无消费方，保留备查——恢复按空分支收起时启用）

const SidebarFolderTree: FC<SidebarFolderTreeProps> = ({ brainSide, readOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { personalFolders, networkFolders, createFolder, updateFolder, removeFolder, seedDefaults } = useFolders(brainSide);
  // 目录级权限（批 D）：仅团队空间露入口，且只给夹创建者/租户 owner/admin（与后端 _can_manage_visibility 同口径）
  const { activeTenant } = useTenants();
  const { user } = useAuth();
  const [permFor, setPermFor] = useState<Folder | null>(null);
  // 默认收起为根级一层，记录被手动开合的节点 id（见 defaultCollapsed 异或语义）
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // 脑分组折叠（09-24 拍板）：both 模式默认收起为一层——只显示「个人脑」「网络脑」
  // 两个组头，点开才展开该脑的目录树；当前路由命中的组强制展开（藏住当前页不合理）
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(['group:personal', 'group:network'])
  );
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // 归档规则编辑弹窗（当前编辑的文件夹）
  const [rulesFor, setRulesFor] = useState<Folder | null>(null);
  const [rulesDraft, setRulesDraft] = useState<{ subtypes: string[]; verification: string[]; tags: string }>({ subtypes: [], verification: [], tags: '' });
  const [rulesError, setRulesError] = useState<string | null>(null);
  // 内联创建/重命名输入行（Electron 不支持 window.prompt/confirm/alert，统一走受控内联组件）
  const [promptState, setPromptState] = useState<
    { kind: 'create'; side: string; parentId: string | null } | { kind: 'rename'; folder: Folder } | null
  >(null);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [promptBusy, setPromptBusy] = useState(false);
  // 内联删除确认行（当前待删除的文件夹）
  const [deleteFor, setDeleteFor] = useState<Folder | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // 树顶部通知条（播种默认目录的提示/错误）
  const [treeNotice, setTreeNotice] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);

  // 默认只展开一层（09-26 拍板，单脑与 both 同口径）：有子节点的节点一律默认收起
  // ——根级可见、子级点开才展（both 模式组头已是一层，展开组后树内同样一层起手）。
  // 手动开合优先于默认（collapsed 与 defaultCollapsed 异或：手动点过即翻转默认）
  const defaultCollapsed = useMemo(() => {
    const s = new Set<string>();
    const walk = (n: FolderNode) => {
      if (n.children.length > 0) s.add(n.id);
      n.children.forEach(walk);
    };
    buildTree(personalFolders || []).forEach(walk);
    buildTree(networkFolders || []).forEach(walk);
    return s;
  }, [personalFolders, networkFolders]);

  // 当前路由命中的文件夹 id（/folders/:id），用于高亮
  const routeMatch = location.pathname.match(/^\/folders\/([^/]+)$/);
  const activeId = routeMatch ? decodeURIComponent(routeMatch[1]) : null;
  // 命中文件夹的祖先链强制展开（默认一层也不能把当前页藏进收起的父级里）
  const activeAncestors = useMemo(() => {
    const s = new Set<string>();
    if (activeId && activeId !== 'none') {
      const byId = new Map(
        [...(personalFolders || []), ...(networkFolders || [])].map((f) => [f.id, f])
      );
      let cur = byId.get(activeId);
      while (cur?.parent_id && byId.has(cur.parent_id)) {
        s.add(cur.parent_id);
        cur = byId.get(cur.parent_id);
      }
    }
    return s;
  }, [activeId, personalFolders, networkFolders]);
  // 当前路由命中的文件夹所在脑分组（命中的组强制展开）
  const activeGroup = useMemo(() => {
    if (!activeId || activeId === 'none') return null;
    if ((personalFolders || []).some((f) => f.id === activeId)) return 'group:personal';
    if ((networkFolders || []).some((f) => f.id === activeId)) return 'group:network';
    return null;
  }, [activeId, personalFolders, networkFolders]);

  const toggleGroupCollapsed = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreatePrompt = (side: string, parentId: string | null) => {
    setPromptState({ kind: 'create', side, parentId });
    setPromptError(null);
  };

  const openRenamePrompt = (folder: Folder) => {
    setPromptState({ kind: 'rename', folder });
    setPromptError(null);
  };

  const closePrompt = () => {
    if (promptBusy) return;
    setPromptState(null);
    setPromptError(null);
  };

  const handlePromptSubmit = async (name: string) => {
    if (!promptState) return;
    // 重命名未改动直接收起，不打请求
    if (promptState.kind === 'rename' && name === promptState.folder.name) {
      setPromptState(null);
      return;
    }
    setPromptBusy(true);
    setPromptError(null);
    try {
      if (promptState.kind === 'create') {
        await createFolder({ name, brain_side: promptState.side, parent_id: promptState.parentId });
      } else {
        await updateFolder({ id: promptState.folder.id, data: { name } });
      }
      setPromptState(null);
    } catch (e: any) {
      setPromptError(e?.response?.data?.detail || e.message || (promptState.kind === 'create' ? '创建失败' : '重命名失败'));
    } finally {
      setPromptBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFor) return;
    setDeleteBusy(true);
    try {
      await removeFolder(deleteFor.id);
      // 删除的正是当前查看的夹时回到笔记列表，避免停在已消失的文件夹页
      if (activeId === deleteFor.id) navigate('/ingest/notes');
      setDeleteFor(null);
    } catch (e: any) {
      setTreeNotice({ kind: 'error', text: e?.response?.data?.detail || e.message || '删除失败' });
      setDeleteFor(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const renderNode = (node: FolderNode, side: string, depth: number) => {
    const hasChildren = node.children.length > 0;
    // 异或语义：默认收起集 ∪ 手动开合记录——用户点过就以用户为准；命中祖先链强制展开
    const isCollapsed = (collapsed.has(node.id) !== defaultCollapsed.has(node.id))
      && !activeAncestors.has(node.id);
    const isActive = activeId === node.id;
    return (
      <div key={node.id}>
        <div
          className={`group relative flex items-center gap-1 py-1 pr-1 rounded-[2px] cursor-pointer text-xs transition-colors ${
            isActive ? 'bg-info/15 text-info' : 'text-text-secondary hover:bg-white/[0.05] hover:text-text-primary'
          }`}
          style={{ paddingLeft: `${4 + depth * 12}px` }}
          onClick={() => navigate(`/folders/${node.id}`)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleCollapsed(node.id); }}
              className="p-0.5 text-text-muted hover:text-text-primary"
            >
              {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <FolderIcon className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{node.name}</span>
          {/* 总内容数 = 直属笔记 + 直属知识单元 + 直属剪藏 + 直属文档 */}
          <span className="text-[10px] text-text-muted">{node.note_count + node.knowledge_count + (node.clip_count || 0) + (node.document_count || 0)}</span>
          {node.auto_rules && (
            <Zap className="w-3 h-3 text-warning shrink-0" />
          )}
          {/* 目录级权限（10-01）：private 锁形 / restricted 成员形，仅对可见者显示 */}
          {node.visibility === 'private' && (
            <Lock className="w-3 h-3 text-text-muted shrink-0" />
          )}
          {node.visibility === 'restricted' && (
            <Users className="w-3 h-3 text-text-muted shrink-0" />
          )}
          {readOnly ? (
            <span className="w-4" />
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === node.id ? null : node.id); }}
              className="p-0.5 rounded-[2px] text-text-muted hover:text-text-primary opacity-0 group-hover:opacity-100 transition-opacity"
              title="更多操作"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          )}
          {menuFor === node.id && (
            <>
              <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setMenuFor(null); }} />
              <div className="absolute right-0 top-full z-50 w-28 bg-bg-secondary border border-border-color rounded-[2px] py-1 shadow-lg">
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuFor(null); openCreatePrompt(side, node.id); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-white/[0.05] hover:text-text-primary"
                >
                  新建子文件夹
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuFor(null); openRenamePrompt(node); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-white/[0.05] hover:text-text-primary"
                >
                  重命名
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuFor(null); openRules(node); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-white/[0.05] hover:text-text-primary"
                >
                  归档规则
                </button>
                {/* 目录级权限：仅团队空间 + 夹创建者/owner/admin（后端同口径二次拦截） */}
                {activeTenant && (node.user_id === user?.id || activeTenant.my_role === 'owner' || activeTenant.my_role === 'admin') && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuFor(null); setPermFor(node); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-white/[0.05] hover:text-text-primary"
                  >
                    权限
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuFor(null); setDeleteFor(node); setTreeNotice(null); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-danger hover:bg-danger/10"
                >
                  删除
                </button>
              </div>
            </>
          )}
        </div>
        {/* 内联重命名输入行（替换 Electron 不支持的 window.prompt） */}
        {promptState?.kind === 'rename' && promptState.folder.id === node.id && (
          <div className="py-0.5 pr-1" style={{ paddingLeft: `${4 + (depth + 1) * 12}px` }}>
            <InlinePrompt
              placeholder="文件夹名称"
              initialValue={promptState.folder.name}
              submitText="保存"
              error={promptError}
              busy={promptBusy}
              onSubmit={handlePromptSubmit}
              onCancel={closePrompt}
            />
          </div>
        )}
        {/* 内联新建子文件夹输入行 */}
        {promptState?.kind === 'create' && promptState.parentId === node.id && (
          <div className="py-0.5 pr-1" style={{ paddingLeft: `${4 + (depth + 1) * 12}px` }}>
            <InlinePrompt
              placeholder="子文件夹名称"
              submitText="创建"
              error={promptError}
              busy={promptBusy}
              onSubmit={handlePromptSubmit}
              onCancel={closePrompt}
            />
          </div>
        )}
        {/* 内联删除确认行（替换 window.confirm） */}
        {deleteFor?.id === node.id && (
          <div className="py-0.5 pr-1" style={{ paddingLeft: `${4 + (depth + 1) * 12}px` }}>
            <InlineConfirm
              text={`确定删除文件夹「${node.name}」吗？其中的子文件夹和内容将上移到上一级。`}
              confirmText="删除"
              busy={deleteBusy}
              onConfirm={handleDelete}
              onCancel={() => !deleteBusy && setDeleteFor(null)}
            />
          </div>
        )}
        {hasChildren && !isCollapsed && node.children.map((child) => renderNode(child, side, depth + 1))}
      </div>
    );
  };

  const handleSeedDefaults = async () => {
    setTreeNotice(null);
    try {
      const r = await seedDefaults();
      if ((r as any)?.data?.created === 0) setTreeNotice({ kind: 'info', text: '目录树已存在，无需重复创建' });
    } catch (e: any) {
      setTreeNotice({ kind: 'error', text: e?.response?.data?.detail || e.message || '创建失败' });
    }
  };

  // 归档规则编辑
  const openRules = (folder: Folder) => {
    const r = folder.auto_rules || {};
    setRulesDraft({
      subtypes: r.subtypes || [],
      verification: r.verification || [],
      tags: (r.tags || []).join('，'),
    });
    setRulesError(null);
    setRulesFor(folder);
  };

  const toggleRuleValue = (dim: 'subtypes' | 'verification', value: string) => {
    setRulesDraft((prev) => ({
      ...prev,
      [dim]: prev[dim].includes(value) ? prev[dim].filter((v) => v !== value) : [...prev[dim], value],
    }));
  };

  const handleSaveRules = async () => {
    if (!rulesFor) return;
    const tags = rulesDraft.tags.split(/[,，、]/).map((t) => t.trim()).filter(Boolean);
    const rules: NonNullable<Folder['auto_rules']> = {};
    if (rulesDraft.subtypes.length) rules.subtypes = rulesDraft.subtypes;
    if (rulesDraft.verification.length) rules.verification = rulesDraft.verification;
    if (tags.length) rules.tags = tags;
    try {
      // 全空 = 清除规则（夹退回用户领地）
      await updateFolder({ id: rulesFor.id, data: { auto_rules: Object.keys(rules).length ? rules : null } });
      setRulesFor(null);
    } catch (e: any) {
      setRulesError(e?.response?.data?.detail || e.message || '保存失败');
    }
  };

  const RULE_OPTIONS: { dim: 'subtypes' | 'verification'; value: string; label: string }[] = [
    { dim: 'subtypes', value: 'concept', label: '概念卡（抽取产物）' },
    { dim: 'subtypes', value: 'collision_result', label: '碰撞火花（碰撞产物）' },
    { dim: 'verification', value: 'confirmed', label: '可信知识（验证通过）' },
  ];

  // 一棵脑的树；both 模式下作为分组渲染（组头带折叠箭头+新建按钮）
  const renderGroup = (title: string | null, side: string, folders: Folder[] | undefined) => {
    const groupId = `group:${side}`;
    const groupCollapsed = title !== null && collapsedGroups.has(groupId) && activeGroup !== groupId;
    return (
    <div>
      {title && (
        <div className="flex items-center justify-between px-1 pt-1.5 pb-0.5">
          <button
            onClick={() => toggleGroupCollapsed(groupId)}
            className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-text-muted hover:text-text-primary transition-colors"
            title={groupCollapsed ? `展开${title}` : `收起${title}`}
          >
            {groupCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {title}
          </button>
          {!readOnly && (
            <button
              onClick={() => openCreatePrompt(side, null)}
              className="p-0.5 rounded-[2px] text-text-muted hover:text-info transition-colors"
              title={`在${title}下新建文件夹`}
            >
              <FolderPlus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {!groupCollapsed && (
      <>
      {/* 根级新建文件夹的内联输入行 */}
      {promptState?.kind === 'create' && promptState.parentId === null && promptState.side === side && (
        <div className="px-1 py-0.5">
          <InlinePrompt
            placeholder="文件夹名称"
            submitText="创建"
            error={promptError}
            busy={promptBusy}
            onSubmit={handlePromptSubmit}
            onCancel={closePrompt}
          />
        </div>
      )}
      {(!folders || folders.length === 0) ? (
        readOnly ? (
          <p className="px-1 py-0.5 text-[10px] text-text-muted">暂无文件夹</p>
        ) : (
          <button
            onClick={handleSeedDefaults}
            className="mx-1 my-0.5 px-2 py-1 rounded-[2px] text-[10px] text-text-muted border border-dashed border-border-color hover:text-info hover:border-info/50 transition-colors"
            title="一键建好默认目录树：收集箱 / 进行中 / 长线深耕 / 素材参考 / 封存（两脑各一套，可删可改名）"
          >
            + 建好默认目录
          </button>
        )
      ) : (
        buildTree(folders).map((node) => renderNode(node, side, 0))
      )}
      </>
      )}
    </div>
    );
  };

  const newRootSide = brainSide === 'network' ? 'network' : 'personal';

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1 px-1">
        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">文件夹</span>
        {brainSide !== 'both' && !readOnly && (
          <button
            onClick={() => openCreatePrompt(newRootSide, null)}
            className="p-0.5 rounded-[2px] text-text-muted hover:text-info transition-colors"
            title="新建文件夹"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {/* 树顶部通知条：播种默认目录的提示与删除等操作的错误（替换 window.alert） */}
      {treeNotice && (
        <div
          className={`mx-1 mb-1 px-2 py-1 rounded-[2px] border text-[10px] leading-snug ${
            treeNotice.kind === 'error'
              ? 'text-danger bg-danger/10 border-danger/20'
              : 'text-text-muted bg-white/[0.03] border-border-color'
          }`}
        >
          {treeNotice.text}
        </div>
      )}
      <div
        className={`flex items-center gap-1.5 px-2 py-1 rounded-[2px] cursor-pointer text-xs transition-colors ${
          activeId === 'none' ? 'bg-info/15 text-info' : 'text-text-secondary hover:bg-white/[0.05] hover:text-text-primary'
        }`}
        onClick={() => navigate('/folders/none')}
      >
        <Inbox className="w-3.5 h-3.5" />
        未归档
      </div>
      {brainSide === 'both' ? (
        <>
          {renderGroup('个人脑', 'personal', personalFolders)}
          {renderGroup('网络脑', 'network', networkFolders)}
        </>
      ) : brainSide === 'network' ? (
        renderGroup(null, 'network', networkFolders)
      ) : (
        renderGroup(null, 'personal', personalFolders)
      )}

      {/* 目录级权限弹窗（批 D）：团队空间节点菜单「权限」打开 */}
      {permFor && activeTenant && (
        <FolderPermDialog folder={permFor} tenantId={activeTenant.id} onClose={() => setPermFor(null)} />
      )}

      {/* 归档规则编辑弹窗：命中规则的未归档内容自动进该夹；全清=退回用户领地 */}
      {rulesFor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={() => setRulesFor(null)}>
          <div className="absolute inset-0 bg-black/80" />
          <div
            className="relative z-10 w-full max-w-[360px] mx-4 glass-card p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-text-primary mb-1">「{rulesFor.name}」的自动归档规则</h3>
            <p className="text-[11px] text-text-secondary mb-3 leading-relaxed">
              命中的未归档内容自动进这个夹（人工归过档的不动）。全部不勾=关闭自动归档。
            </p>
            <div className="space-y-1.5 mb-3">
              {RULE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rulesDraft[opt.dim].includes(opt.value)}
                    onChange={() => toggleRuleValue(opt.dim, opt.value)}
                    className="accent-fusion-primary"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <label className="text-xs text-text-secondary block mb-1">含以下标签的内容（逗号分隔）：</label>
            <input
              type="text"
              value={rulesDraft.tags}
              onChange={(e) => setRulesDraft((prev) => ({ ...prev, tags: e.target.value }))}
              placeholder="如：读书，AI"
              className="w-full bg-bg-tertiary border border-border-color rounded-[2px] px-3 py-1.5 text-xs text-text-primary placeholder-text-secondary focus:outline-none focus:border-info mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setRulesFor(null)}
                className="flex-1 px-3 py-1.5 rounded-[2px] text-xs bg-bg-tertiary border border-border-color text-text-secondary hover:text-text-primary transition-colors"
              >
                取消
              </button>
              <button onClick={handleSaveRules} className="flex-1 btn-primary text-xs py-1.5">
                保存规则
              </button>
            </div>
            {rulesError && <div className="mt-2 text-[11px] text-danger leading-snug">{rulesError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarFolderTree;
